import React, { useCallback, useMemo, useState } from "react";
import { Card, Flex, Text, toast } from "@jsq/ds/core";
import { Form, FormTextInput, FormNumberInput, FormDatePicker } from "@jsq/ds/forms";
import { Column, ColumnContainer } from "@jsq/ds/layouts";
import { useSubmitPayableAndPayment } from "#components/PayableWorkflow/hooks/useSubmitPayableAndPayment";
import { getTodayInETAsLocalDate } from "../../../../helpers/get-today-in-et";
import { EntityBankAccountType } from "../../../EntitySelector/types";
import { DocumentTracker } from "../../../PayeeWorkflows/components/DocumentTracker";
import { FormStepFooter } from "../../../PayeeWorkflows/components/FormStepFooter";
import { FormEntitySelector } from "../../components/FormEntitySelector";
import { PayeeDisplay } from "../../components/PayeeDisplay";
import { PayableActionTypes, PayableStepActionTypes } from "../../Context/constants";
import {
  usePayableDataContext,
  usePayableDataDispatch,
  usePayableWorkflowDispatch,
} from "../../Context/PayableContext";
import {
  stepColumnContainerStyles,
  payeeDetailsColumnStyles,
  scrollableColumnStyles,
} from "../../styles_vanilla.css";
import {
  PayableFormFields,
  paymentDetailsStepValidationSchema,
  PaymentDetailsStepFormValues,
  PaymentDetailsStepInitialValues,
} from "../../types";
import { PayableStepProps } from "../types";
import { entitySelectionStyles } from "./styles_vanilla.css";

/**
 * Restricts input to a maximum of 2 decimal places for currency values.
 * Returns true if the input should be allowed, false if it should be blocked.
 */
const isValidCurrencyInput = (value: string): boolean => {
  if (value === "" || value === ".") return true;
  const decimalIndex = value.indexOf(".");
  if (decimalIndex === -1) return true;
  const decimalPlaces = value.length - decimalIndex - 1;
  return decimalPlaces <= 2;
};

export const PaymentDetailsStep: React.FC<PayableStepProps> = ({ onComplete }) => {
  const payableData = usePayableDataContext();
  const dispatch = usePayableDataDispatch();
  const stepDispatch = usePayableWorkflowDispatch();

  const [selectedEntityName, setSelectedEntityName] = useState<string>(
    payableData.entityName ?? "",
  );

  const { submit, loading } = useSubmitPayableAndPayment({
    onSuccess: onComplete,
  });

  const handleEntitySelect = useCallback(
    (entityId: string, entityName: string, bankAccount?: EntityBankAccountType | null) => {
      setSelectedEntityName(entityName);
      dispatch({
        type: PayableActionTypes.SET_ENTITY,
        payload: {
          entityId,
          entityName,
          entityAchCompanyIdPresent: bankAccount?.achCompanyIdPresent,
          entityPaymentCapabilities: bankAccount?.paymentCapabilities,
        },
      });
    },
    [dispatch],
  );

  const handleBack = useCallback(() => {
    stepDispatch({ type: PayableStepActionTypes.DECREMENT_STEP });
  }, [stepDispatch]);

  const handleFormSubmit = useCallback(
    async (values: Partial<PaymentDetailsStepFormValues>) => {
      // Prepare payment details from form values (pass directly to avoid async context update issues)
      const paymentDetails = {
        paymentAmount: values.paymentAmount ?? 0,
        paymentDate: values.paymentDate ?? null,
        paymentDescription: values.paymentDescription ?? null,
      };

      // Sets payment details even though it's not used in submit function in case submit fails but user goes back
      // and forth on workflow
      dispatch({
        type: PayableActionTypes.SET_PAYMENT_DETAILS,
        payload: {
          paymentAmount: paymentDetails.paymentAmount,
          paymentDate: paymentDetails.paymentDate,
          paymentDescription: paymentDetails.paymentDescription ?? undefined,
        },
      });

      // Submit both payable and payment with form values passed directly
      // Note: Entity was already dispatched to context via handleEntitySelect when user selected it
      const result = await submit(paymentDetails);

      if (result.error) {
        toast.error(result.error);
      }
    },
    [dispatch, submit],
  );

  const firstDocument = payableData.invoiceDocuments?.[0];

  // Memoize todaysDate to avoid recreating on every render.
  // Anchored to the ET settlement date, which can differ from the user's local date.
  const todaysDate = useMemo(() => getTodayInETAsLocalDate(), []);

  // Memoize initialValues to prevent form reset on every render when enableReinitialize is true
  // Only recompute when the actual data from context changes
  const initialValues: PaymentDetailsStepInitialValues = useMemo(
    () => ({
      [PayableFormFields.EntityId]: payableData.entityId ?? "",
      // Use undefined instead of 0 so placeholder "0.00" displays when field is empty
      paymentAmount: payableData.paymentAmount ?? undefined,
      paymentDate: payableData.paymentDate ?? todaysDate,
      paymentDescription: payableData.paymentDescription ?? "",
    }),
    [
      payableData.entityId,
      payableData.paymentAmount,
      payableData.paymentDate,
      payableData.paymentDescription,
      todaysDate,
    ],
  );

  return (
    <Form
      yupValidationSchema={paymentDetailsStepValidationSchema}
      initialValues={initialValues}
      onSubmit={handleFormSubmit}
      submitOnlyChangedValues={false}
    >
      <ColumnContainer className={stepColumnContainerStyles}>
        {/* Left Column - Document Display */}
        <Column size="fluid">
          {firstDocument && (
            <DocumentTracker
              documentName={firstDocument.name}
              documentUrl={firstDocument.localUrl}
            />
          )}
        </Column>

        {/* Right Column - Form */}
        <Column
          size="fluid"
          className={`${payeeDetailsColumnStyles} ${scrollableColumnStyles}`}
        >
          <Card title="Payment details">
            <Flex flexDirection="column" gap={20}>
              {/* Send to section */}
              <Flex flexDirection="column" gap={8}>
                <Text>Send to:</Text>
                {payableData.payeeId && <PayeeDisplay payeeId={payableData.payeeId} />}
              </Flex>

              {/* Payment amount */}
              <Flex flexDirection="column" gap={8}>
                <Text>Payment amount</Text>
                <FormNumberInput
                  name="paymentAmount"
                  placeholder="0.00"
                  isFullWidth
                  preAdornment="$"
                  min={0}
                  step={0.01}
                  onKeyDown={(e) => {
                    // Allow control keys (backspace, delete, arrows, tab, etc.)
                    if (
                      e.key === "Backspace" ||
                      e.key === "Delete" ||
                      e.key === "Tab" ||
                      e.key === "Escape" ||
                      e.key === "Enter" ||
                      e.key === "ArrowLeft" ||
                      e.key === "ArrowRight" ||
                      e.key === "ArrowUp" ||
                      e.key === "ArrowDown" ||
                      e.key === "Home" ||
                      e.key === "End" ||
                      (e.ctrlKey &&
                        (e.key === "a" || e.key === "c" || e.key === "v" || e.key === "x"))
                    ) {
                      return;
                    }
                    // Check if adding this key would exceed 2 decimal places
                    const input = e.currentTarget;
                    const currentValue = input.value;
                    const selectionStart = input.selectionStart ?? currentValue.length;
                    const selectionEnd = input.selectionEnd ?? currentValue.length;
                    const newValue =
                      currentValue.slice(0, selectionStart) +
                      e.key +
                      currentValue.slice(selectionEnd);
                    if (!isValidCurrencyInput(newValue)) {
                      e.preventDefault();
                    }
                  }}
                  onWheel={(e) => e.currentTarget.blur()}
                  onFocus={(e) => e.target.select()}
                />
              </Flex>

              {/* Payment date */}
              <Flex flexDirection="column" gap={8}>
                <Text>Payment date</Text>
                <FormDatePicker
                  name="paymentDate"
                  placeholderText="Select payment date"
                  dateFormat="MMM d, yyyy"
                  hasIcon
                  isFull
                  minDate={todaysDate}
                />
              </Flex>

              {/* Payment description */}
              <Flex flexDirection="column" gap={8}>
                <Text>Payment description</Text>
                <FormTextInput
                  name="paymentDescription"
                  placeholder="Enter payment description"
                  isFullWidth
                  maxLength={80}
                />
              </Flex>

              {/* Send from section - uses payeeSectionDividerStyles for top border separator */}
              <Flex flexDirection="column" gap={16} className={entitySelectionStyles}>
                <Text>Send from:</Text>
                <FormEntitySelector
                  arenaId={payableData.arenaId ?? ""}
                  selectedEntityName={selectedEntityName}
                  onEntitySelect={handleEntitySelect}
                  isFundAdmin={true}
                  hasBankAccount={true}
                  fieldName={PayableFormFields.EntityId}
                  label="Investment entity"
                />
              </Flex>
            </Flex>
          </Card>

          <FormStepFooter
            onBack={handleBack}
            submitText="Submit for review"
            submitDisabled={loading}
          />
        </Column>
      </ColumnContainer>
    </Form>
  );
};

export default PaymentDetailsStep;
